import React, { useEffect, useMemo, useState } from 'react';
import { Card, Icon, Intent, Tag } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useHistory, useParams } from 'react-router-dom';
import { useObserver } from 'mobx-react-lite';
import { DateDropdownPicker } from '@zaiusinc/hera';
import { DurationInputArg2 } from 'moment';
import MainContainer from './MainContainer';
import { ZipApi } from '../../lib/ZipApi';
import { dataSyncMetricsStore } from '../../stores/DataSyncMetricsStore';
import DataSyncDetailsStore from '../../stores/DataSyncDetailsStore';
import { directoryStore } from '../../stores/DirectoryStore';
import { DataSyncHealthStatus, DataSyncMetricsBucket } from '../../types/DataSyncMetrics';
import SparklineChart from './components/SparklineChart';
import DetailMetricsChart from './components/DetailMetricsChart';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { showToasts } from '../../lib/toaster';
import styles from './DataSyncAnalyticsPage.module.sass';

const RANGE_OPTIONS: Array<{ key: string; unit: DurationInputArg2; quantity: number; label: string }> = [
  { key: 'days_7', unit: 'days', quantity: 7, label: 'Past 7 days' },
  { key: 'days_15', unit: 'days', quantity: 15, label: 'Past 15 days' },
  { key: 'days_30', unit: 'days', quantity: 30, label: 'Past 30 days' },
];

interface TimeFrameFilter {
  timeFrame: string;
  start: string | null;
  end: string | null;
}

interface StageCardModel {
  key: 'received' | 'delivered' | 'ingest' | 'load' | 'mapping' | 'delivery';
  name: string;
  series: number[];
  trend: {
    delta: number;
    deltaPercent: number;
  };
  value: number;
}

const toNumber = (value?: number | string): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const formatInteger = (value: number) => value.toLocaleString('en-US');

const buildSeriesFromBuckets = (
  buckets: DataSyncMetricsBucket[],
  valueSelector: (bucket: DataSyncMetricsBucket) => number
): number[] => {
  const totalsByBucket = new Map<string, number>();

  buckets.forEach((bucket) => {
    const bucketKey = bucket.bucket || '';
    totalsByBucket.set(bucketKey, (totalsByBucket.get(bucketKey) || 0) + valueSelector(bucket));
  });

  return Array.from(totalsByBucket.entries())
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([, total]) => total);
};

const buildPeriodChange = (currentTotal: number, previousTotal: number) => {
  const delta = currentTotal - previousTotal;
  const deltaPercent = previousTotal > 0 ? (delta / previousTotal) * 100 : 0;
  return { delta, deltaPercent };
};

const healthIntent = (status: DataSyncHealthStatus): Intent => {
  if (status === DataSyncHealthStatus.FAILING) {
    return Intent.DANGER;
  }
  if (status === DataSyncHealthStatus.WARNING) {
    return Intent.WARNING;
  }
  return Intent.SUCCESS;
};

const healthLabel = (status: DataSyncHealthStatus): string => {
  if (status === DataSyncHealthStatus.HEALTHY) {
    return 'Healthy';
  }
  if (status === DataSyncHealthStatus.WARNING) {
    return 'Warning';
  }
  return 'Failing';
};

export const DataSyncAnalyticsPage = () => {
  const { dataSyncId } = useParams<{ dataSyncId: string }>();
  const history = useHistory();
  const [timeframeFilter, setTimeframeFilter] = useState<TimeFrameFilter>({
    timeFrame: 'days_7',
    start: null,
    end: null,
  });
  const [selectedStageKey, setSelectedStageKey] = useState<StageCardModel['key']>('received');
  const [detailsStore] = useState(() => new DataSyncDetailsStore(dataSyncId));
  const [previousBuckets, setPreviousBuckets] = useState<DataSyncMetricsBucket[]>([]);
  const [previousBucketsLoading, setPreviousBucketsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    setHasLoadedOnce(false);
  }, [dataSyncId]);

  const parseDateToEpoch = (value: unknown): number | undefined => {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      return Math.floor(value.getTime() / 1000);
    }

    if (typeof value === 'string') {
      const parsed = new Date(value).getTime();
      if (Number.isFinite(parsed)) {
        return Math.floor(parsed / 1000);
      }
    }

    return undefined;
  };

  const rangeEpochs = useMemo(() => {
    const now = new Date();
    const customFrom = parseDateToEpoch(timeframeFilter.start);
    const customTo = parseDateToEpoch(timeframeFilter.end);

    if (customFrom && customTo) {
      return {
        fromEpoch: customFrom,
        toEpoch: customTo,
      };
    }

    const dayCountByKey: Record<string, number> = {
      days_7: 7,
      days_15: 15,
      days_30: 30,
    };

    const days = dayCountByKey[timeframeFilter.timeFrame] || 7;
    const toEpoch = Math.floor(now.getTime() / 1000);
    const fromEpoch = toEpoch - (days * 24 * 60 * 60);
    return { fromEpoch, toEpoch };
  }, [timeframeFilter.end, timeframeFilter.start, timeframeFilter.timeFrame]);

  useEffect(() => {
    let cancelled = false;

    const fetchMetrics = async () => {
      try {
        if (!directoryStore.trackerId) {
          return;
        }

        setPreviousBucketsLoading(true);
        setPreviousBuckets([]);

        const duration = rangeEpochs.toEpoch - rangeEpochs.fromEpoch + 1;
        const previousFromEpoch = rangeEpochs.fromEpoch - duration;
        const previousToEpoch = rangeEpochs.fromEpoch - 1;

        const [, previousResponse] = await Promise.all([
          dataSyncMetricsStore.fetchMetrics(directoryStore.trackerId, [dataSyncId], {
            fromEpoch: rangeEpochs.fromEpoch,
            toEpoch: rangeEpochs.toEpoch,
            granularity: 'METRICS_GRANULARITY_DAILY',
          }),
          ZipApi.getDataSyncMetrics(
            directoryStore.trackerId,
            [dataSyncId],
            previousFromEpoch,
            previousToEpoch,
            'METRICS_GRANULARITY_DAILY'
          ),
        ]);

        if (!cancelled) {
          setPreviousBuckets(previousResponse.metrics_buckets || []);
        }
      } catch (e) {
        showToasts([{ intent: 'danger', message: `Failed to load analytics. ${e}` }]);
      } finally {
        if (!cancelled) {
          setPreviousBucketsLoading(false);
        }
      }
    };

    fetchMetrics();

    return () => {
      cancelled = true;
    };
  }, [dataSyncId, rangeEpochs]);

  return useObserver(() => {
    const trackerId = directoryStore.trackerId;
    const dataSync = detailsStore.dataSync;
    const summary = dataSyncMetricsStore.getSummary(dataSyncId);
    const buckets = dataSyncMetricsStore.getBuckets(dataSyncId);
    const loading = !trackerId
      || detailsStore.loading
      || !dataSync
      || previousBucketsLoading
      || (dataSyncMetricsStore.loading && !summary);

    if (!loading && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }

    const ingestBuckets = buckets.filter((bucket) => bucket.stage === 'ingest');
    const loadBuckets = buckets.filter((bucket) => bucket.stage === 'load');
    const mappingBuckets = buckets.filter((bucket) => bucket.stage === 'map');
    const exportBuckets = buckets.filter((bucket) => bucket.stage === 'export');

    const previousIngestBuckets = previousBuckets.filter((bucket) => bucket.stage === 'ingest');
    const previousLoadBuckets = previousBuckets.filter((bucket) => bucket.stage === 'load');
    const previousMappingBuckets = previousBuckets.filter((bucket) => bucket.stage === 'map');
    const previousExportBuckets = previousBuckets.filter((bucket) => bucket.stage === 'export');

    const ingestSuccessSeries = buildSeriesFromBuckets(ingestBuckets, (bucket) => toNumber(bucket.total_success_count));
    const exportSuccessSeries = buildSeriesFromBuckets(exportBuckets, (bucket) => toNumber(bucket.total_success_count));

    const ingestErrorSeries = buildSeriesFromBuckets(ingestBuckets, (bucket) => toNumber(bucket.total_error_count));
    const loadErrorSeries = buildSeriesFromBuckets(loadBuckets, (bucket) => toNumber(bucket.total_error_count));
    const mappingErrorSeries = buildSeriesFromBuckets(mappingBuckets, (bucket) => toNumber(bucket.total_error_count));
    const deliveryErrorSeries = buildSeriesFromBuckets(exportBuckets, (bucket) => toNumber(bucket.total_error_count));

    const totalReceived = ingestSuccessSeries.reduce((sum, value) => sum + value, 0);
    const totalDelivered = exportSuccessSeries.reduce((sum, value) => sum + value, 0);

    const previousTotalReceived = previousIngestBuckets
      .reduce((sum, bucket) => sum + toNumber(bucket.total_success_count), 0);
    const previousTotalDelivered = previousExportBuckets
      .reduce((sum, bucket) => sum + toNumber(bucket.total_success_count), 0);

    const totalFailedOnIngest = ingestErrorSeries.reduce((sum, value) => sum + value, 0);
    const totalFailedOnLoad = loadErrorSeries.reduce((sum, value) => sum + value, 0);
    const totalFailedOnMapping = mappingErrorSeries.reduce((sum, value) => sum + value, 0);
    const totalFailedOnDelivery = deliveryErrorSeries.reduce((sum, value) => sum + value, 0);

    const previousFailedOnIngest = previousIngestBuckets
      .reduce((sum, bucket) => sum + toNumber(bucket.total_error_count), 0);
    const previousFailedOnLoad = previousLoadBuckets
      .reduce((sum, bucket) => sum + toNumber(bucket.total_error_count), 0);
    const previousFailedOnMapping = previousMappingBuckets
      .reduce((sum, bucket) => sum + toNumber(bucket.total_error_count), 0);
    const previousFailedOnDelivery = previousExportBuckets
      .reduce((sum, bucket) => sum + toNumber(bucket.total_error_count), 0);

    const receivedTrend = buildPeriodChange(totalReceived, previousTotalReceived);
    const deliveredTrend = buildPeriodChange(totalDelivered, previousTotalDelivered);
    const ingestErrorTrend = buildPeriodChange(totalFailedOnIngest, previousFailedOnIngest);
    const loadErrorTrend = buildPeriodChange(totalFailedOnLoad, previousFailedOnLoad);
    const mappingErrorTrend = buildPeriodChange(totalFailedOnMapping, previousFailedOnMapping);
    const deliveryErrorTrend = buildPeriodChange(totalFailedOnDelivery, previousFailedOnDelivery);

    const sparklineData = ingestSuccessSeries;
    const cards: StageCardModel[] = [
      {
        key: 'received',
        name: 'Events received',
        series: ingestSuccessSeries,
        trend: receivedTrend,
        value: totalReceived,
      },
      {
        key: 'delivered',
        name: 'Events delivered',
        series: exportSuccessSeries,
        trend: deliveredTrend,
        value: totalDelivered,
      },
      {
        key: 'ingest',
        name: 'Ingest',
        series: ingestErrorSeries,
        trend: ingestErrorTrend,
        value: totalFailedOnIngest,
      },
      {
        key: 'load',
        name: 'Load',
        series: loadErrorSeries,
        trend: loadErrorTrend,
        value: totalFailedOnLoad,
      },
      {
        key: 'mapping',
        name: 'Mapping',
        series: mappingErrorSeries,
        trend: mappingErrorTrend,
        value: totalFailedOnMapping,
      },
      {
        key: 'delivery',
        name: 'Delivery',
        series: deliveryErrorSeries,
        trend: deliveryErrorTrend,
        value: totalFailedOnDelivery,
      },
    ];
    const selectedStage = cards.find((stage) => stage.key === selectedStageKey) || cards[0];
    const detailTitle = selectedStage.key === 'received'
      ? 'Successfully received'
      : selectedStage.key === 'delivered'
        ? 'Successfully delivered'
        : `${selectedStage.name} stage`;
    const detailDescription = selectedStage.key === 'received'
      ? 'Events successfully received from source'
      : selectedStage.key === 'delivered'
        ? 'Events successfully delivered to destination'
        : `Performance for ${selectedStage.name.toLowerCase()} over the selected date range`;
    const detailValueLabel = (selectedStage.key === 'received' || selectedStage.key === 'delivered')
      ? (selectedStage.key === 'received' ? 'events received' : 'events delivered')
      : 'events failed';
    const detailPrimaryValue = selectedStage.value;
    const detailSecondaryLine = (selectedStage.key === 'received' || selectedStage.key === 'delivered')
      ? (summary ? `${summary.successRate.toFixed(0)}% of all events` : '0% of all events')
      : `Change: ${formatInteger(selectedStage.trend.delta)} (${selectedStage.trend.deltaPercent.toFixed(1)}%)`;
    const detailSeries = selectedStage.series.length > 0 ? selectedStage.series : sparklineData;

    if (loading && !hasLoadedOnce) {
      return (
        <MainContainer fixedHeight={true}>
          <LoadingIndicator />
        </MainContainer>
      );
    }

    return (
      <MainContainer scrollableContent={true}>
        <div className={styles.container}>
          <button className={styles.backLink} onClick={() => history.push('/data_syncs')}>
            <Icon icon={IconNames.CHEVRON_LEFT} />
            <span>Sync Manager</span>
          </button>

          <div className={styles.topRow}>
            <div className={styles.titleWrap}>
              <h1 className={styles.title}>{dataSync?.name || 'Data Sync'}</h1>
              {summary && (
                <Tag intent={healthIntent(summary.healthStatus)} round={true}>
                  {healthLabel(summary.healthStatus)}
                </Tag>
              )}
            </div>

            <div className={styles.rangeControl}>
              <span className={styles.rangeControlLabel}>Date range</span>
              <DateDropdownPicker
                dateRangeOptions={RANGE_OPTIONS}
                onChange={(option: { key: string; start?: string; end?: string }) => {
                  setTimeframeFilter((previous) => {
                    const next = {
                      timeFrame: option.key,
                      start: option.start || null,
                      end: option.end || null,
                    };

                    if (
                      previous.timeFrame === next.timeFrame
                      && previous.start === next.start
                      && previous.end === next.end
                    ) {
                      return previous;
                    }

                    return next;
                  });
                }}
                initialActiveOptionKey={timeframeFilter.timeFrame}
                value={
                  timeframeFilter.start && timeframeFilter.end
                    ? [timeframeFilter.start, timeframeFilter.end]
                    : undefined
                }
                position={'right'}
                loading={false}
                popover={{
                  position: 'bottom-right',
                  className: styles.analyticsTimeframeFilter,
                }}
              />
            </div>
          </div>

          <h2 className={styles.sectionTitle}>Overview</h2>

          <div className={styles.overviewGrid}>
              <Card
                className={`${styles.largeCard} ${selectedStage.key === 'received' ? styles.activeCard : ''}`}
                onClick={() => setSelectedStageKey('received')}
                role="button"
                tabIndex={0}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedStageKey('received');
                  }
                }}
              >
              <div className={styles.cardLabel}>Events received</div>
              <div className={styles.cardValue}>{formatInteger(totalReceived)} received</div>
              <SparklineChart data={sparklineData} width="100%" height={66} lineColor="#596a8b" fillColor="#ebeff8" />
              <div className={styles.trendLine}>
                Change: {formatInteger(receivedTrend.delta)}
                {' '}
                <span className={receivedTrend.delta < 0 ? styles.trendNegative : styles.trendPositive}>
                  ({receivedTrend.deltaPercent.toFixed(1)}%)
                </span>
              </div>
            </Card>

            <Card
              className={`${styles.largeCard} ${selectedStage.key === 'delivered' ? styles.activeCard : ''}`}
              onClick={() => setSelectedStageKey('delivered')}
              role="button"
              tabIndex={0}
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedStageKey('delivered');
                }
              }}
            >
              <div className={styles.cardLabel}>Events delivered</div>
              <div className={styles.cardValue}>{formatInteger(totalDelivered)} delivered</div>
              <SparklineChart data={exportSuccessSeries} width="100%" height={66} lineColor="#2f5be7" fillColor="#d9e5ff" />
              <div className={styles.trendLine}>
                Change: {formatInteger(deliveredTrend.delta)}
                {' '}
                <span className={deliveredTrend.delta < 0 ? styles.trendNegative : styles.trendPositive}>
                  ({deliveredTrend.deltaPercent.toFixed(1)}%)
                </span>
              </div>
            </Card>
          </div>

          <div className={styles.stageGrid}>
            {cards.filter((card) => card.key !== 'received' && card.key !== 'delivered').map((stage) => (
              <Card
                key={stage.name}
                className={`${styles.stageCard} ${selectedStage.key === stage.key ? styles.activeCard : ''}`}
                onClick={() => setSelectedStageKey(stage.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedStageKey(stage.key);
                  }
                }}
              >
                <div className={styles.cardLabel}>{stage.name}</div>
                <div className={styles.cardSubValue}>{formatInteger(stage.value)} events failed</div>
                <SparklineChart data={stage.series} width="100%" height={45} lineColor="#596a8b" fillColor="#eef2fa" />
                <div className={styles.trendLine}>
                  Change: {formatInteger(stage.trend.delta)}
                  {' '}
                  <span className={stage.trend.delta < 0 ? styles.trendNegative : styles.trendPositive}>
                    ({stage.trend.deltaPercent.toFixed(1)}%)
                  </span>
                </div>
              </Card>
            ))}
          </div>

          <h2 className={styles.sectionTitle}>Detail</h2>
          <Card className={styles.detailCard}>
            <div className={styles.detailLeft}>
              <h3>{detailTitle}</h3>
              <p>{detailDescription}</p>
              <strong>{formatInteger(detailPrimaryValue)} {detailValueLabel}</strong>
              <p>{detailSecondaryLine}</p>
            </div>
            <div className={styles.detailRight}>
              <div className={styles.detailMetric}>{formatInteger(detailPrimaryValue)}</div>
              <DetailMetricsChart data={detailSeries} />
            </div>
          </Card>
        </div>
      </MainContainer>
    );
  });
};

export default DataSyncAnalyticsPage;
